import React, { useEffect, useState, KeyboardEvent, useCallback, useMemo } from "react";
import {
    Box,
    Grid,
    TextField,
    Button,
    Typography,
    Chip,
    IconButton,
    InputAdornment,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";



import { AddCircleOutline, Cancel, Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import moment from "moment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { WebPage } from "../../../core/models/db/web-page";
import { SEO } from "../../../core/models/db";
import DragDrop from "../../components/drag-drop/DragDrop";
import TemplateGeneratorButton from "../progam/components/TemplateGenerationButton";
import ContentBlocksList from "../progam/components/ContentBlockList";
import { NumericFormatCustom, NumericFormat100PercentCustom, RoundedSquareIcon, MuiLoadingButton } from "../../components";
import FloatingActions from "../progam/components/FloatingAction";



interface ProgramFormProps {
    loading?: boolean;
    initialWebPage: WebPage; // Program con o sin SEO
    // checkProgramSlug: (id: string, slug: string) => Promise<boolean>;
    onSubmit: (
        program: WebPage & { seo?: SEO },
        imageItem: {
            oldImage: string;
            image: string;
            nameImage: string;
        }
    ) => void;
}

const SpartanForm = ({
    loading,
    initialWebPage,
    onSubmit,
}: ProgramFormProps) => {
    const { t } = useTranslation();

    const [isEditing, setIsEditing] = useState(!initialWebPage?.id);

    // Manejo de la imagen del Program
    const [image, setImage] = useState(initialWebPage?.image || "");
    const [oldImage, setOldImage] = useState(initialWebPage?.image || "");
    const [imageName, setImageName] = useState("");

    // Manejo de slug
    const [slugError, setSlugError] = useState<string | null>(null);
    const [slugChecked, setSlugChecked] = useState<null | boolean>(null);

    /**
  * Aquí tenemos los contentBlocks como un estado local
  * Se inicializa parseando el `jsonContent` que venga del initialProgram
  */
    const [contentBlocks, setContentBlocks] = useState(() => {
        if (initialWebPage?.jsonContent) {
            try {
                const parsed = JSON.parse(initialWebPage.jsonContent);
                return Array.isArray(parsed) ? parsed : [];
            } catch (error) {
                console.error("Error parsing jsonContent:", error);
                return [];
            }
        }
        return [];
    });

    // Manejo de las keywords en SEO
    const [keywordChips, setKeywordChips] = useState<string[]>(
        initialWebPage?.seo?.keywords
            ? initialWebPage.seo.keywords.split(",").map((kw) => kw.trim()).filter(Boolean)
            : []
    );

    const [newKeyword, setNewKeyword] = useState("");
    const [keywordsError, setKeywordsError] = useState<string | null>(null);

    /**
     * =========================================================================
     *  1) DEFINIMOS EL ESQUEMA DE VALIDACIÓN (Formik + Yup)
     * =========================================================================
     */
    const validationSchema = Yup.object().shape({
        titleMenu: Yup.string()
            .required("Nombre menú requerido")
            .max(100, "Máximo 100 caracteres."),
        title: Yup.string().required("Título requerido").max(100, "Máximo 100 caracteres."),
        subtitle: Yup.string().max(150, "Máximo 150 caracteres."),
        description: Yup.string().max(1000, "Máximo 1000 caracteres."),
        // price: Yup.string()
        //     .required("Precio es requerido")
        //     .matches(/^\d+(\.\d{1,2})?$/, "Precio inválido"),
        // discountPercentage: Yup.string()
        //     .required("El porcentaje de descuento es requerido")
        //     .matches(/^\d{1,5}(\.\d{1,2})?$/, "Porcentaje inválido")
        //     .test("is-valid-percentage", "El porcentaje debe estar entre 0 y 100", (value) => {
        //         if (!value) return false;
        //         const num = parseFloat(value);
        //         return num >= 0 && num <= 100;
        //     }),
        externalLink: Yup.string()
            .required("URL externa es requerida")
            .transform((value) => {
                if (!value) return value;
                const trimmedValue = value.trim();
                if (
                    !trimmedValue.startsWith("http://") &&
                    !trimmedValue.startsWith("https://")
                ) {
                    return `https://${trimmedValue}`;
                }
                return trimmedValue;
            })
            .url("URL externa inválida"),

        // seo: Yup.object().shape({
        //     slug: Yup.string()
        //         .max(50, "Máximo 50 caracteres")
        //         .matches(/^\/[a-z0-9]+\/[a-z0-9]+(?:-[a-z0-9]+)*$/, "Formato de slug inválido.")
        //         .required("El slug es obligatorio.")

        //         .test(
        //             "slug-availability",
        //             "Este slug ya está en uso.", // Mensaje de error personalizado
        //             async (value) => {
        //                 if (slugError) return false;
        //                 return true;
        //             }
        //         ),
        //     title: Yup.string().max(100, "Máximo 100 caracteres en SEO title."),
        //     description: Yup.string().max(300, "Máximo 300 caracteres en SEO description."),
        // }),
    });
    /**
     * =========================================================================
     *  2) INICIALIZAMOS FORM + FORM VALUES
     * =========================================================================
     */
    const formik = useFormik({
        initialValues: {
            id: initialWebPage?.id,
            titleMenu: initialWebPage?.titleMenu || "",
            title: initialWebPage?.title || "",
            subtitle: initialWebPage?.subtitle || "",
            description: initialWebPage?.description || "",
            price: initialWebPage?.price || "", // Agregado
            discountPercentage: initialWebPage?.discountPercentage || 0, // Agregado
            externalLink: initialWebPage?.externalLink || "", // Agregado
            jsonContent: initialWebPage?.jsonContent || "",
            isValid: initialWebPage?.isValid ?? true,
            seo: {
                id: initialWebPage?.seo?.id,
                slug: initialWebPage?.seo?.slug || "",
                title: initialWebPage?.seo?.title || "",
                description: initialWebPage?.seo?.description || "",
                keywords: initialWebPage?.seo?.keywords || "",
            },
            /**
             * Agregamos categoryPrograms en el form.
             * Asumimos que si existía antes, no queremos perder su 'id' pivot.
             * Si no hay nada, lo inicializamos como un array vacío (opcional).
             */
        },
        validationSchema,
        onSubmit: (values) => {
            const finalJsonContent = JSON.stringify(contentBlocks);

            console.log("values", values);

            // 1) Convertir array de chips a string
            const finalKeywords = keywordChips.join(",");

            // 2) Construir el Program a guardar
            const programToSave: WebPage & { seo?: SEO } = {
                ...values,
                jsonContent: finalJsonContent,
                image,
                seo: {
                    ...values.seo,
                    keywords: finalKeywords, // Guardamos como string
                },
            };

            console.log(programToSave);
            // return;

            onSubmit(programToSave, { oldImage, image, nameImage: values.title });
        },
        enableReinitialize: true,
    });

    // Al cambiar initialProgram, refrescar chips
    useEffect(() => {
        if (initialWebPage?.seo?.keywords) {
            const chipsArr = initialWebPage.seo.keywords
                .split(",")
                .map((kw) => kw.trim())
                .filter(Boolean);
            setKeywordChips(chipsArr);
        } else {
            setKeywordChips([]);
        }
    }, [initialWebPage]);

    /**
     * =========================================================================
     *  3) MANEJO DE LA IMAGEN
     * =========================================================================
     */
    const handleFiles = (files: any[]) => {
        if (files.length === 0) {
            setImage("");
            formik.setFieldValue("image", "");
        } else if (files?.[0].blob) {
            const file = files[0];
            const url = URL.createObjectURL(file.blob);
            setImage(url);
            formik.setFieldValue("image", url);
        }
    };

    const changeToBackup = () => {
        setImage(null);
        setImageName("");
        setTimeout(() => {
            setImage(initialWebPage?.image);
            setImageName(
                initialWebPage?.image?.substring(
                    initialWebPage?.image?.lastIndexOf("/") + 1
                )
            );
            setOldImage(initialWebPage?.image);
        }, 200);
    };

    /**
     * =========================================================================
     *  4) BOTONES DE EDICIÓN / CANCELAR
     * =========================================================================
     */
    const handleCancelClick = () => {
        formik.resetForm();
        setContentBlocks(() => {
            // Restaurar contentBlocks iniciales:
            if (initialWebPage?.jsonContent) {
                try {
                    const parsed = JSON.parse(initialWebPage.jsonContent);
                    return Array.isArray(parsed) ? parsed : [];
                } catch (error) {
                    return [];
                }
            }
            return [];
        });
        setImage(initialWebPage?.image || "");
        setOldImage(initialWebPage?.image || "");
        setIsEditing(false);
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    /**
     * =========================================================================
     *  5) MANEJO DE KEYWORDS (CHIPS)
     * =========================================================================
     */
    const handleAddKeyword = () => {
        const kwTrim = newKeyword.trim();
        if (!kwTrim) {
            setKeywordsError("La palabra clave no puede estar vacía.");
            return;
        }
        if (keywordChips.includes(kwTrim)) {
            setKeywordsError("Esta palabra clave ya existe en la lista.");
            return;
        }
        if (keywordChips.length >= 10) {
            setKeywordsError("No puedes añadir más de 10 palabras clave.");
            return;
        }
        const isValidKeyword = /^[a-zA-Z0-9_-]+$/.test(kwTrim);
        if (!isValidKeyword) {
            setKeywordsError(
                "Las palabras clave solo pueden contener caracteres alfanuméricos, guion medio y guion bajo."
            );
            return;
        }
        setKeywordChips((prev) => [...prev, kwTrim]);
        setNewKeyword("");
        setKeywordsError(null);
    };

    const handleRemoveKeyword = (kw: string) => {
        setKeywordChips((prev) => prev.filter((k) => k !== kw));
    };

    const handleKeywordKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddKeyword();
        }
    };



    /**
     * =========================================================================
     *  7) RENDER DEL FORM
     * =========================================================================
     */
    return (
        <form onSubmit={formik.handleSubmit} style={{ marginRight: 0, marginLeft: 0, padding: 0 }}>
            {/* <pre>{JSON.stringify(formik.values, null, 2)}</pre> */}
            <Box >

                <DragDrop
                    id="program-image"
                    valueDefault={image}
                    disabled={loading || !isEditing}
                    directoryNameList={["program", "images"]}
                    maxSizeMB={2}
                    typeAllowed={["image/*"]}
                    isOnlyOneFile={true}
                    isBlob={true}
                    specialCase={true}
                    showFileList={false}
                    handleFiles={handleFiles}
                />

                <Grid container spacing={2} mt={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            label={
                                <>
                                    Nombre menú  <Typography component="span" color="error">*</Typography>
                                </>
                            }
                            name="titleMenu"
                            value={formik.values.titleMenu}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.touched.titleMenu && formik.errors.titleMenu)}
                            helperText={
                                formik.touched.titleMenu && typeof formik.errors.titleMenu === "string"
                                    ? formik.errors.titleMenu
                                    : undefined
                            }
                            // required
                            disabled={loading || (!isEditing && !!initialWebPage?.id)}
                            fullWidth
                        />
                    </Grid>

                </Grid>

                <Grid container spacing={2} mt={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            label={
                                <>
                                    Título  <Typography component="span" color="error">*</Typography>
                                </>
                            }
                            name="title"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.touched.title && formik.errors.title)}
                            helperText={
                                formik.touched.title && typeof formik.errors.title === "string"
                                    ? formik.errors.title
                                    : undefined
                            }
                            // required
                            disabled={loading || (!isEditing && !!initialWebPage?.id)}
                            fullWidth
                        />
                    </Grid>


                    <Grid item xs={12} sm={6}>
                        <TextField

                            variant="standard"
                            label={
                                <>
                                    Subtítulo
                                </>
                            }
                            name="subtitle"
                            value={formik.values.subtitle}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.touched.subtitle && formik.errors.subtitle)}
                            helperText={
                                formik.touched.subtitle && typeof formik.errors.subtitle === "string"
                                    ? formik.errors.subtitle
                                    : undefined
                            }
                            disabled={loading || (!isEditing && !!initialWebPage?.id)}
                            fullWidth
                        />
                    </Grid>


                    <Grid item xs={12}>
                        <TextField
                            variant="standard"
                            label={
                                <>
                                    Descripción
                                </>
                            }
                            name="description"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={Boolean(formik.touched.description && formik.errors.description)}
                            helperText={
                                formik.touched.description && typeof formik.errors.description === "string"
                                    ? formik.errors.description
                                    : undefined
                            }
                            multiline
                            rows={4}
                            disabled={loading || (!isEditing && !!initialWebPage?.id)}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            label={
                                <>
                                    URL Externa <Typography component="span" color="error">*</Typography>
                                </>
                            }
                            name="externalLink"
                            value={formik.values.externalLink.replace(/^https?:\/\//, "")}
                            onChange={(e) => {
                                const value = e.target.value.trim();
                                // Evitar que el usuario agregue http:// o https:// manualmente
                                formik.setFieldValue(
                                    "externalLink",
                                    value.startsWith("http://") || value.startsWith("https://")
                                        ? value
                                        : value
                                );
                            }}
                            onBlur={(e) => {
                                const value = e.target.value.trim();
                                if (
                                    !value.startsWith("http://") &&
                                    !value.startsWith("https://")
                                ) {
                                    formik.setFieldValue("externalLink", `https://${value}`);
                                }
                                formik.handleBlur(e);
                            }}
                            error={Boolean(formik.touched.externalLink && formik.errors.externalLink)}
                            helperText={
                                formik.touched.externalLink && typeof formik.errors.externalLink === "string"
                                    ? formik.errors.externalLink
                                    : "URL externa del sponsor (sin https://, se añadirá automáticamente)"
                            }
                            // required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography variant="body2" color="textSecondary">
                                            https://
                                        </Typography>
                                    </InputAdornment>
                                ),
                            }}
                            disabled={loading || (!isEditing && !!initialWebPage?.id)}
                            fullWidth
                        />
                    </Grid>



                    <Grid item xs={12}>
                        <Box sx={{ border: '1px solid #ccc', padding: 2, borderRadius: 2, mt: 2 }}>

                            <Grid container spacing={2}>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        variant="standard"
                                        label={
                                            <>
                                                Precio
                                            </>
                                        }
                                        name="price"
                                        value={formik.values.price}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.touched.price && formik.errors.price)}
                                        helperText={
                                            formik.touched.price && typeof formik.errors.price === "string"
                                                ? formik.errors.price
                                                : undefined
                                        }
                                        disabled={loading || (!isEditing && !!initialWebPage?.id)}
                                        fullWidth
                                        InputProps={{
                                            inputComponent: NumericFormatCustom as any,
                                            inputProps: {
                                                noDecimals: false,
                                                noThousandSeparator: false,
                                                maxLength: 10,
                                            },
                                        }}
                                    />
                                </Grid>


                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        variant="standard"
                                        label={
                                            <>
                                                Porcentaje de Descuento
                                            </>
                                        }
                                        name="discountPercentage"
                                        value={formik.values.discountPercentage}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.touched.discountPercentage && formik.errors.discountPercentage)}
                                        helperText={
                                            formik.touched.discountPercentage &&
                                                typeof formik.errors.discountPercentage === "string"
                                                ? formik.errors.discountPercentage
                                                : undefined
                                        }
                                        disabled={loading || (!isEditing && !!initialWebPage?.id)}
                                        fullWidth
                                        InputProps={{
                                            inputComponent: NumericFormat100PercentCustom as any,
                                            inputProps: {
                                                noDecimals: false, // Permite decimales
                                                noThousandSeparator: true,
                                                // maxLength se maneja internamente en el componente
                                            },
                                        }}
                                    />
                                </Grid>


                                <Grid item xs={12} sm={12}>
                                    <TextField
                                        variant="standard"
                                        label="Precio Final (Descuento Aplicado)"
                                        value={formik.values.price && formik.values.discountPercentage
                                            ? (
                                                parseFloat(formik.values.price) *
                                                (1 - parseFloat(formik.values.discountPercentage) / 100)
                                            ).toFixed(2)
                                            : ''}
                                        InputProps={{
                                            readOnly: true,
                                            disabled: !isEditing,
                                            style: { fontSize: '1.4rem', fontWeight: 'bold' }, // Estilo más grande y llamativo
                                        }}
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                </Grid>

                <Grid item xs={12} sm={12} className="mt-4">
                    <FormControlLabel
                        control={
                            <Checkbox
                                icon={<RoundedSquareIcon checked={false} disabled={!isEditing} />}
                                checkedIcon={<RoundedSquareIcon checked={true} disabled={!isEditing} />}
                                disabled={loading || (!isEditing && !!initialWebPage?.id)}
                                name="isValid"
                                checked={formik.values.isValid}
                                onChange={(e) => {
                                    // invertimos el valor
                                    formik.setFieldValue("isValid", e?.target?.checked);
                                }}
                            />
                        }
                        label={
                            <>
                                Mostrar página
                                <Typography variant="caption" color="textSecondary" display="block">
                                    Esta opción permite mostrar la página en la web.
                                </Typography>
                            </>
                        }
                    />
                </Grid>

                <Box mt={4}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        paddingBottom: 1,
                        marginBottom: 2
                    }}>
                        <Typography variant="h6" gutterBottom>
                            Bloques de Contenido
                        </Typography>
                    </Box>
                    <ContentBlocksList
                        blocks={contentBlocks}
                        setBlocks={setContentBlocks}
                        disabled={loading || (!isEditing && !!initialWebPage?.id)}

                        isSpartan={true}
                    />
                </Box>


                {/* BOTONES DE ACCIÓN */}
                <FloatingActions
                    isNew={!initialWebPage?.id}
                    isEditing={isEditing}
                    loading={loading || false}
                    isValid={formik.isValid}
                    slugChecked={slugChecked}
                    onCreate={() => formik.submitForm()}
                    onEdit={handleEditClick}
                    onCancel={handleCancelClick}
                    onSave={() => formik.submitForm()}
                />
                {/* <Box display="flex" justifyContent="flex-end" gap={2} mt={5}>
                    {!initialWebPage?.id && (
                        <div className="w-auto">
                            <MuiLoadingButton
                                title="Crear"
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={!formik.isValid || loading}
                                isLoading={loading}
                            />
                        </div>
                    )}
                    {initialWebPage?.id && !isEditing && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleEditClick}
                            disabled={loading}
                        >
                            Editar
                        </Button>
                    )}
                    {initialWebPage?.id && isEditing && (
                        <div className="flex flex-row gap-2">
                            <Button
                                className="flex-1"
                                variant="outlined"
                                color="primary"
                                onClick={handleCancelClick}
                                disabled={loading}
                            >
                                Cancelar
                            </Button>
                            <div className="w-auto">
                                <MuiLoadingButton
                                    title="Guardar"
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={!formik.isValid || loading}
                                    isLoading={loading}
                                />
                            </div>
                        </div>
                    )}
                </Box> */}
            </Box>
        </form>
    );
};

export default SpartanForm;